import React, { FC, PropsWithChildren, useMemo } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Button } from '@studykik/ui-library';
import { useTranslation } from 'react-i18next';
import {
  ExpenseSubmitData,
  PaymentConfigCategory,
  PaymentConfigSubcategory,
} from '../../models/paymentConfig';
import {
  AddExpenseButton,
  PaymentSubmissionButtonContainer,
  PaymenSubmissionDropdownField,
  PaymenSubmissionFieldContainer,
  PaymenSubmissionFieldLabel,
  PaymenSubmissionFieldWrapper,
  PaymentSubmissionExpensesContainer,
  PaymentSubmissionExpensesList,
  PaymentSubmissionExpensesListCell,
  PaymentSubmissionExpensesListRow,
  PaymentSubmissionExpensesTotalsLabel,
  PaymentSubmissionTotal,
  PaymentSubmissionTotalLabel,
  PaymentSubmissionTotalValue,
} from './styles';
import formatCurrency from '../../utils/formatCurrency';

type AddRequestFormProps = PropsWithChildren & {
  isSavingSubmission: boolean;
  selectedPaymentCategory: PaymentConfigCategory | null;
  selectedPaymentSubcategory: PaymentConfigSubcategory | null;
  expenses: ExpenseSubmitData[];
  onClickPaymentCategoryField: () => void;
  onClickPaymentSubcategoryField: () => void;
  onClickAddExpenseButton: () => void;
  onClickSaveSubmission: () => void;
};

const AddRequestFormComponent: FC<AddRequestFormProps> = ({
  isSavingSubmission,
  selectedPaymentCategory,
  selectedPaymentSubcategory,
  expenses,
  onClickPaymentCategoryField,
  onClickPaymentSubcategoryField,
  onClickAddExpenseButton,
  onClickSaveSubmission,
}) => {
  const { t } = useTranslation();

  const totalAmount = useMemo(
    () =>
      Number(selectedPaymentSubcategory?.amount || 0) +
      expenses.reduce<number>((acc, { amount }) => acc + Number(amount), 0),

    [selectedPaymentSubcategory, expenses]
  );

  return (
    <>
      <PaymenSubmissionFieldContainer>
        <PaymenSubmissionFieldLabel required>
          {t('Payment Category')}
        </PaymenSubmissionFieldLabel>
        <PaymenSubmissionFieldWrapper>
          <PaymenSubmissionDropdownField
            disabled={isSavingSubmission}
            onClick={onClickPaymentCategoryField}
          >
            {selectedPaymentCategory
              ? selectedPaymentCategory.name
              : t('Select Option')}
          </PaymenSubmissionDropdownField>
        </PaymenSubmissionFieldWrapper>
      </PaymenSubmissionFieldContainer>

      <PaymenSubmissionFieldContainer>
        <PaymenSubmissionFieldLabel required>
          {t('Payment Subcategory')}
        </PaymenSubmissionFieldLabel>
        <PaymenSubmissionFieldWrapper>
          <PaymenSubmissionDropdownField
            disabled={!selectedPaymentCategory || isSavingSubmission}
            onClick={onClickPaymentSubcategoryField}
          >
            {selectedPaymentSubcategory
              ? selectedPaymentSubcategory.name
              : t('Select Option')}
          </PaymenSubmissionDropdownField>
        </PaymenSubmissionFieldWrapper>
      </PaymenSubmissionFieldContainer>
      {selectedPaymentSubcategory && (
        <PaymentSubmissionExpensesContainer>
          <PaymentSubmissionExpensesTotalsLabel>
            {t('Your Request Totals')}
          </PaymentSubmissionExpensesTotalsLabel>
          <PaymentSubmissionExpensesList>
            <PaymentSubmissionExpensesListRow>
              <PaymentSubmissionExpensesListCell>
                {selectedPaymentSubcategory.name}
              </PaymentSubmissionExpensesListCell>
              <PaymentSubmissionExpensesListCell>
                {formatCurrency({
                  amount: selectedPaymentSubcategory.amount,
                  currency: selectedPaymentSubcategory.currency,
                })}
              </PaymentSubmissionExpensesListCell>
            </PaymentSubmissionExpensesListRow>
            {expenses.map(({ expenseCategoryName, amount }) => (
              // no unique identifier for key
              <PaymentSubmissionExpensesListRow>
                <PaymentSubmissionExpensesListCell>
                  {expenseCategoryName}
                </PaymentSubmissionExpensesListCell>
                <PaymentSubmissionExpensesListCell>
                  {formatCurrency({
                    amount,
                    currency: selectedPaymentSubcategory.currency,
                  })}
                </PaymentSubmissionExpensesListCell>
              </PaymentSubmissionExpensesListRow>
            ))}
          </PaymentSubmissionExpensesList>
          {selectedPaymentSubcategory.config?.allowForExpenses && (
            <AddExpenseButton
              startIcon={<AddIcon />}
              variant="outlined"
              size="medium"
              onClick={onClickAddExpenseButton}
            >
              {t('Expense')}
            </AddExpenseButton>
          )}
        </PaymentSubmissionExpensesContainer>
      )}
      <PaymentSubmissionButtonContainer>
        <PaymentSubmissionTotal>
          <PaymentSubmissionTotalLabel>
            {t('Total Request')}
          </PaymentSubmissionTotalLabel>
          <PaymentSubmissionTotalValue>
            {!selectedPaymentSubcategory
              ? formatCurrency({
                  amount: totalAmount,
                  currency: 'USD',
                })
              : formatCurrency({
                  amount: totalAmount,
                  currency: selectedPaymentSubcategory?.currency,
                })}
          </PaymentSubmissionTotalValue>
        </PaymentSubmissionTotal>
        <Button
          disabled={
            !selectedPaymentCategory ||
            !selectedPaymentSubcategory ||
            isSavingSubmission ||
            totalAmount === 0
          }
          isLoading={isSavingSubmission}
          onClick={onClickSaveSubmission}
        >
          {t('Submit')}
        </Button>
      </PaymentSubmissionButtonContainer>
    </>
  );
};

export default AddRequestFormComponent;
